import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack,
  Download,
  Visibility,
  AttachFile,
  PictureAsPdf,
  Image,
  InsertDriveFile,
  TableChart,
  Link as LinkIcon,
  OpenInNew,
  ExpandMore,
  PictureAsPdfOutlined,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { transactionsApi, attachmentsApi } from '../services/api';
import AttachmentPreviewDialog, { getPreviewType } from './AttachmentPreviewDialog';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { generateTransactionPdf } from '../utils/pdfExport';
import TransactionChain from './TransactionChain';

const TYPE_LABELS = {
  PurchaseOrder: 'Purchase Order',
  SalesOrder: 'Sales Order',
  Bill: 'Bill',
  Invoice: 'Invoice',
  VendorCredit: 'Vendor Credit',
  BillPaymentCheck: 'Bill Payment (Check)',
  BillPaymentCreditCard: 'Bill Payment (CC)',
  ReceivePayment: 'Receive Payment',
  ItemReceipt: 'Item Receipt',
};

const TYPE_COLORS = {
  PurchaseOrder: '#32578b',
  SalesOrder: '#2e7d32',
  Bill: '#ed6c02',
  Invoice: '#9c27b0',
  VendorCredit: '#d32f2f',
  BillPaymentCheck: '#0277bd',
  BillPaymentCreditCard: '#00838f',
  ReceivePayment: '#558b2f',
  ItemReceipt: '#6a1b9a',
};

function getFileIcon(fileType) {
  if (!fileType) return <InsertDriveFile />;
  if (fileType.includes('pdf')) return <PictureAsPdf sx={{ color: '#d32f2f' }} />;
  if (fileType.includes('image')) return <Image sx={{ color: '#388e3c' }} />;
  if (fileType.includes('sheet') || fileType.includes('excel') || fileType.includes('csv'))
    return <TableChart sx={{ color: '#1b5e20' }} />;
  return <InsertDriveFile sx={{ color: '#757575' }} />;
}

function formatFileType(att) {
  const type = (att.fileType || '').toLowerCase();
  const ext = (att.fileName || '').split('.').pop().toLowerCase();

  if (type.includes('pdf') || ext === 'pdf') return 'PDF';
  if (type === 'image/jpeg' || ext === 'jpg' || ext === 'jpeg') return 'JPEG Image';
  if (type === 'image/png' || ext === 'png') return 'PNG Image';
  if (type === 'image/gif' || ext === 'gif') return 'GIF Image';
  if (type.startsWith('image/') || ['webp', 'bmp', 'svg', 'tiff', 'tif'].includes(ext)) return 'Image';
  if (type.includes('wordprocessingml') || ext === 'docx') return 'Word Document';
  if (type === 'application/msword' || ext === 'doc') return 'Word Document';
  if (type.includes('spreadsheetml') || ext === 'xlsx') return 'Excel Spreadsheet';
  if (type.includes('ms-excel') || ext === 'xls') return 'Excel Spreadsheet';
  if (type === 'text/csv' || ext === 'csv') return 'CSV';
  if (type.includes('ms-outlook') || ext === 'msg') return 'Outlook Email';
  if (type === 'message/rfc822' || ext === 'eml') return 'Email';
  if (type === 'text/plain' || ext === 'txt') return 'Text File';
  if (type.includes('text/html') || ext === 'htm' || ext === 'html') return 'HTML';
  if (ext === 'rtf' || type.includes('rtf')) return 'Rich Text';
  if (ext === 'zip' || type.includes('zip')) return 'ZIP Archive';
  if (ext === 'oxps' || ext === 'xps') return 'XPS Document';
  // For application/octet-stream or anything else, use the extension if present
  if (ext && ext !== att.fileName?.toLowerCase()) return ext.toUpperCase();
  return 'Unknown';
}

function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function OverflowTooltip({ text, sx }) {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback((el) => {
    if (el) {
      textRef.current = el;
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, []);

  return (
    <Tooltip title={isOverflowing ? text : ''} placement="top-start">
      <Typography
        ref={checkOverflow}
        variant="body2"
        sx={{
          maxWidth: { xs: 150, sm: 400 },
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...sx,
        }}
      >
        {text}
      </Typography>
    </Tooltip>
  );
}

function TransactionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pushCrumb } = useBreadcrumbs();
  const [txn, setTxn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    const loadTransaction = async () => {
      try {
        setLoading(true);
        const data = await transactionsApi.getById(id);
        setTxn(data);
        const typeLabel = TYPE_LABELS[data.transactionType] || data.transactionType;
        pushCrumb(`/transaction/${id}`, `${typeLabel} #${data.refNumber}`);
      } catch (err) {
        console.error('Error loading transaction:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadTransaction();
  }, [id, pushCrumb]);

  const handleDownload = async (attachment) => {
    try {
      const data = await attachmentsApi.getPreviewUrl(attachment.id);
      const response = await fetch(data.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handlePreview = (attachment) => {
    setPreviewAttachment(attachment);
    setPreviewOpen(true);
  };

  const handleExportPdf = async () => {
    if (!printRef.current || !txn) return;
    setExporting(true);
    try {
      const typeLabel = TYPE_LABELS[txn.transactionType] || txn.transactionType;
      await generateTransactionPdf(printRef.current, `${typeLabel} #${txn.refNumber}`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !txn) {
    return (
      <div className="page-container">
        <Paper sx={{ p: 4, maxWidth: 800, width: '100%', textAlign: 'center' }}>
          <Typography color="error" gutterBottom>
            {error || 'Document not found'}
          </Typography>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/')}>
            Back to Search
          </Button>
        </Paper>
      </div>
    );
  }

  const typeLabel = TYPE_LABELS[txn.transactionType] || txn.transactionType;
  const entityLabel = txn.entityType === 'Customer' ? 'Customer' : 'Vendor';

  return (
    <div className="page-container">
      <div ref={printRef} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, width: '100%', maxWidth: 1000 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBack />
          </IconButton>
          <Chip
            label={typeLabel}
            size="small"
            style={{
              backgroundColor: TYPE_COLORS[txn.transactionType] || '#757575',
              color: '#ffffff',
              fontWeight: 'bold',
            }}
          />
          <Typography variant="h5" fontWeight="bold" color="primary">
            #{txn.refNumber}
          </Typography>
          <Chip
            icon={<AttachFile />}
            label={`${txn.attachments?.length || 0} attachment${txn.attachments?.length !== 1 ? 's' : ''}`}
            color="primary"
            variant="outlined"
          />
          <Tooltip title="Export as PDF">
            <IconButton onClick={handleExportPdf} disabled={exporting} size="small">
              <PictureAsPdfOutlined />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">{entityLabel}</Typography>
            {txn.entityId ? (
              <RouterLink to={`/entity/${txn.entityId}`} style={{ textDecoration: 'none' }}>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  sx={{ color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                >
                  {txn.entityName}
                </Typography>
              </RouterLink>
            ) : (
              <Typography variant="body1" fontWeight="medium">{txn.entityName}</Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="caption" color="text.secondary">Date</Typography>
            <Typography variant="body1">
              {txn.txnDate ? format(new Date(txn.txnDate), 'MMMM d, yyyy') : '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="caption" color="text.secondary">Amount</Typography>
            <Typography variant="body1" fontWeight="medium">
              {txn.amount ? `$${parseFloat(txn.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
            </Typography>
          </Grid>
          {txn.dueDate && (
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" color="text.secondary">Due Date</Typography>
              <Typography variant="body1">{format(new Date(txn.dueDate), 'MMMM d, yyyy')}</Typography>
            </Grid>
          )}
          {txn.terms && (
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" color="text.secondary">Terms</Typography>
              <Typography variant="body2">{txn.terms}</Typography>
            </Grid>
          )}
          {txn.className && (
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" color="text.secondary">Class</Typography>
              <Typography variant="body2">{txn.className}</Typography>
            </Grid>
          )}
          {txn.status && (
            <Grid item xs={12} sm={3}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Typography variant="body2">{txn.status}</Typography>
            </Grid>
          )}
          {txn.memo && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Memo</Typography>
              <Typography variant="body2">{txn.memo}</Typography>
            </Grid>
          )}
          {txn.shipAddress && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary">Ship To</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{txn.shipAddress}</Typography>
            </Grid>
          )}
          {txn.rawData?.customFields && Object.keys(txn.rawData.customFields).length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Custom Fields</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                {Object.entries(txn.rawData.customFields).map(([key, value]) => (
                  <Chip key={key} label={`${key}: ${value}`} size="small" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}
          {(txn.timeCreated || txn.timeModified) && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.disabled">
                {txn.timeCreated && `Created: ${format(new Date(txn.timeCreated), 'MM/dd/yyyy h:mm a')}`}
                {txn.timeCreated && txn.timeModified && ' · '}
                {txn.timeModified && `Modified: ${format(new Date(txn.timeModified), 'MM/dd/yyyy h:mm a')}`}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Known data issue warning */}
      {txn.hasDuplicateRefWarning && (
        <Alert severity="warning" sx={{ width: '100%', maxWidth: 1000 }}>
          <strong>Known data issue:</strong> Another {typeLabel} shares this ref number ({txn.refNumber}).
          Attachments may be linked to the wrong document. This will be corrected in the next data import before go-live.
        </Alert>
      )}

      {/* Line Items */}
      {txn.lineItems?.length > 0 && (
        <Paper sx={{ width: '100%', maxWidth: 1000, position: 'relative' }}>
          {/* Gradient scroll hint */}
          <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'absolute', right: 0, top: 0, bottom: 0, width: 36, background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.85))', pointerEvents: 'none', zIndex: 1, borderRadius: '0 4px 4px 0' }} />
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6">Line Items ({txn.lineItems.length})</Typography>
          </Box>
          <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }}>#</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Description</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {txn.lineItems.map((li) => (
                  <TableRow key={li.id} hover>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{li.lineNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{li.itemName || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {li.description ? (
                        <OverflowTooltip text={li.description} />
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{li.quantity ? parseFloat(li.quantity).toLocaleString() : '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {li.rate ? `$${parseFloat(li.rate).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {li.amount ? `$${parseFloat(li.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total row — uses separate cells to match hidden Description column */}
                <TableRow>
                  <TableCell colSpan={2} sx={{ borderBottom: 'none' }} />
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, borderBottom: 'none' }} />
                  <TableCell colSpan={2} align="right">
                    <Typography variant="body2" fontWeight="bold">Total</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      ${txn.lineItems
                        .reduce((sum, li) => sum + (li.amount ? parseFloat(li.amount) : 0), 0)
                        .toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Linked Transactions */}
      {txn.linkedTransactions?.length > 0 && (
        <Paper sx={{ width: '100%', maxWidth: 1000, position: 'relative' }}>
          {/* Gradient scroll hint */}
          <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'absolute', right: 0, top: 0, bottom: 0, width: 36, background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.85))', pointerEvents: 'none', zIndex: 1, borderRadius: '0 4px 4px 0' }} />
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6">
              <LinkIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
              Linked Transactions ({txn.linkedTransactions.length})
            </Typography>
          </Box>
          <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 80 }}>Type</TableCell>
                  <TableCell>Ref #</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell sx={{ width: 40 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {txn.linkedTransactions.map((lt) => {
                  const ltTypeLabel = TYPE_LABELS[lt.linkedTxnType] || TYPE_LABELS[lt.resolvedType] || lt.linkedTxnType;
                  const ltTypeColor = TYPE_COLORS[lt.linkedTxnType] || TYPE_COLORS[lt.resolvedType] || '#757575';
                  const isResolved = lt.resolvedId != null;

                  return (
                    <TableRow
                      key={lt.id}
                      hover
                      sx={{ cursor: isResolved ? 'pointer' : 'default' }}
                      onClick={(e) => {
                        if (!isResolved) return;
                        if (e.ctrlKey || e.metaKey) {
                          window.open(`/transaction/${lt.resolvedId}`, '_blank');
                        } else {
                          navigate(`/transaction/${lt.resolvedId}`);
                        }
                      }}
                      onAuxClick={(e) => {
                        if (e.button === 1 && isResolved) { e.preventDefault(); window.open(`/transaction/${lt.resolvedId}`, '_blank'); }
                      }}
                    >
                      <TableCell>
                        <span
                          style={{
                            display: 'inline-block',
                            backgroundColor: ltTypeColor,
                            color: '#ffffff',
                            fontWeight: 'bold',
                            fontSize: '0.7rem',
                            padding: '2px 8px',
                            borderRadius: '16px',
                            lineHeight: '1.4',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {ltTypeLabel}
                        </span>
                      </TableCell>
                      <TableCell>
                        {isResolved ? (
                          <RouterLink
                            to={`/transaction/${lt.resolvedId}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ textDecoration: 'none' }}
                          >
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {lt.resolvedRefNumber || lt.linkedRefNumber || '—'}
                            </Typography>
                          </RouterLink>
                        ) : (
                          <Typography variant="body2" fontWeight="bold" color="text.secondary">
                            {lt.resolvedRefNumber || lt.linkedRefNumber || '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">
                          {lt.linkedTxnDate ? format(new Date(lt.linkedTxnDate), 'MM/dd/yyyy') : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {lt.amount ? `$${parseFloat(lt.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {isResolved ? (
                          <OpenInNew sx={{ fontSize: 16, color: 'text.disabled' }} />
                        ) : (
                          <Tooltip title="This transaction is not in the archive">
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Transaction Chain */}
      <TransactionChain transactionId={parseInt(id)} />

      {/* Attachments */}
      <Paper sx={{ width: '100%', maxWidth: 1000, position: 'relative' }}>
        {/* Gradient scroll hint */}
        <Box sx={{ display: { xs: 'block', sm: 'none' }, position: 'absolute', right: 0, top: 0, bottom: 0, width: 36, background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.85))', pointerEvents: 'none', zIndex: 1, borderRadius: '0 4px 4px 0' }} />
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6">Attachments</Typography>
        </Box>

        {txn.attachments?.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No attachments for this document.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Size</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Uploaded</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {txn.attachments?.map((att) => {
                  const canPreview = getPreviewType(att) !== null;
                  return (
                    <TableRow key={att.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getFileIcon(att.fileType)}
                          <Typography variant="body2">{att.fileName}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileType(att)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">{formatFileSize(att.fileSize)}</Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">
                          {att.createdAt ? format(new Date(att.createdAt), 'MM/dd/yyyy') : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {canPreview ? (
                          <Tooltip title="Preview">
                            <IconButton size="small" onClick={() => handlePreview(att)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="This file type cannot be previewed — download to view">
                            <span>
                              <IconButton size="small" disabled>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Download">
                          <IconButton size="small" onClick={() => handleDownload(att)}>
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      </div>{/* end printRef */}

      {/* All Fields (rawData explorer) */}
      {txn.rawData && Object.keys(txn.rawData).length > 0 && (
        <Accordion sx={{ width: '100%', maxWidth: 1000 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">All Fields</Typography>
              <Typography variant="caption" color="text.secondary">
                Raw data from the original QuickBooks record
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Table size="small">
                <TableBody>
                  {Object.entries(txn.rawData)
                    .filter(([, v]) => v != null && v !== '' && !(typeof v === 'object' && Object.keys(v).length === 0))
                    .map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell sx={{ width: 200, verticalAlign: 'top', fontWeight: 500, color: 'text.secondary' }}>
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}
                      </TableCell>
                      <TableCell>
                        {typeof value === 'object' ? (
                          <Box component="pre" sx={{ m: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                            {JSON.stringify(value, null, 2)}
                          </Box>
                        ) : (
                          <Typography variant="body2">{String(value)}</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Preview Dialog */}
      <AttachmentPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        attachment={previewAttachment}
        onDownload={handleDownload}
      />
    </div>
  );
}

export default TransactionDetail;
